import './EventForm.css';
import React, { useState, useEffect } from 'react';
import { ModalController } from '../../../../components/Modal/ControlledModal';
import { EventBase, addEvent, updateEventDetails } from '../../../../model/event';
import { TextField } from '../../../../components/Form/TextField/TextField';
import { Label } from '../../../../components/Form/Label/Label';
import { Datepicker } from '../../../../components/Form/Datepicker/Datepicker';
import { sortBy, uniqBy } from 'lodash';
import { handleError } from '../../../../model/notification';
import { AsyncSearch } from '../../../../components/Form/AsyncSearch/AsyncSearch';
import { VenueLayout } from '../../../../model/venue';
import { Select } from '../../../../components/Form/Select/Select';
import { api } from '../../../../model/store';
import { useChanges, Form } from '../../../../components/Form/Form';
import {
  getCalculatedEventPublishedUntil,
  formatDateTime,
  addHoursToDate,
  formatDuration,
  getDateRangeDuration,
} from '../../../../util/util';
import { t } from '../../../../locales/translate';
import { Button } from '../../../../components/Button/Button';
import { Stack } from '../../../../components/Stack/Stack';
import { Spacer } from '../../../../components/Spacer/Spacer';
import { Switcher } from '../../../../components/Form/Switcher/Switcher';
import { SLUG_REGEX, regexToPattern } from '../../../../model/regex';
import { TextArea } from '../../../../components/Form/TextArea/TextArea';

interface Props {
  controller: ModalController<EventBase>;
}

interface EventFormChanges extends EventBase {
  venueId?: string;
  title?: string;
  description?: string | null;
  launchSaleAt?: Date;
  productVariantsActiveFrom?: Date | null;
}

export function EventForm({ controller }: Props): JSX.Element {
  const [changes, setChanges, event, trackChanges] = useChanges<EventFormChanges>(controller.model);

  function handleInput(model: EventFormChanges, field: keyof EventFormChanges): void {
    if (field === 'happeningFrom') {
      if (model.happeningFrom && (!model.happeningUntil || model.happeningUntil < model.happeningFrom)) {
        trackChanges({ happeningUntil: addHoursToDate(model.happeningFrom, 1) }, 'happeningUntil');
      }
    }
    trackChanges(model, field);
  }

  async function handleOnSubmit(): Promise<void> {
    try {
      if (event.happeningFrom && event.happeningUntil) {
        if (event.happeningFrom > event.happeningUntil) {
          throw new Error(t.errors.errorStartEndRange);
        }
        if (event.publishedFrom && event.publishedFrom > event.happeningFrom) {
          throw new Error(t.errors.errorPublishRange);
        }
      }

      if (event.id) {
        await updateEventDetails({
          eventId: event.id,
          changes: {
            ...changes,
          },
        });
        controller.close();
      } else {
        await addEvent(changes);
      }
    } catch (error) {
      handleError(error);
    }
  }

  // HANDLE VENUES
  const [loadingVenueLayouts, setLoadingVenueLayouts] = useState(false);
  const [venueLayouts, setVenueLayouts] = useState<VenueLayout[]>([]);

  async function loadVenueLayouts(venueId: string): Promise<void> {
    setLoadingVenueLayouts(true);
    setChanges({
      ...changes,
      venueLayoutId: undefined,
    });
    try {
      // TODO: In theory, this could return more than 100 via pagination
      const resp = await api.indexVenueLayouts({ venueId, perPage: 100 });
      setVenueLayouts(resp.data);
    } catch (error) {
      handleError(error);
    }
    setLoadingVenueLayouts(false);
  }

  useEffect(() => {
    if (changes.venueId) {
      loadVenueLayouts(changes.venueId);
    } else {
      setVenueLayouts([]);
      trackChanges(
        {
          venueLayoutId: undefined,
        },
        'venueLayoutId'
      );
    }
  }, [changes.venueId]);

  const calculatedPublishedUntil = getCalculatedEventPublishedUntil(event.happeningUntil || event.happeningFrom);

  const startAndPublishUntilOutOfSync = event.publishedUntil
    ? event.publishedUntil?.getTime() !== calculatedPublishedUntil?.getTime()
    : false;

  function updatePublishUntil(): void {
    if (calculatedPublishedUntil) {
      trackChanges({ publishedUntil: calculatedPublishedUntil }, 'publishedUntil');
    }
  }

  const selectedVenueLayout = venueLayouts.find((vl) => vl.id === event.venueLayoutId);

  return (
    <Form model={event} onInput={handleInput}>
      {(form) => {
        controller.configure({
          onSubmit: handleOnSubmit,
          canSubmit: form.valid,
        });
        return (
          <Stack>
            {/* { Venues } */}
            {!event.id && (
              <>
                <Label title={t.model.venue._name} />
                <AsyncSearch name="venueId" label={t.model.venue._plural} searchIn={['venue']} required />
                <Select
                  label={t.model.venueLayout._name}
                  name="venueLayoutId"
                  options={sortBy(venueLayouts, 'name')
                    .filter((vl) => vl.archived === false && vl.cloned === false)
                    .map((e) => ({
                      value: e.id,
                      disabled: !e.readyForEvents,
                      label: [
                        e.name,
                        e.pricingCategories &&
                          `(${e.pricingCategories.length}) ${
                            e.pricingCategories.length > 1
                              ? t.model.pricingCategory._plural
                              : t.model.pricingCategory._name
                          }`,
                        `(${e.capacity}) ${t.model.address.capacity}`,
                      ]
                        .filter(Boolean)
                        .join(' - '),
                    }))}
                  allowEmpty
                  required
                  loading={loadingVenueLayouts}
                  loadingMsg={t.actions.loading(t.model.venueLayout._plural)}
                />
                {selectedVenueLayout?.requiresMapSelection && (
                  <Switcher
                    value={event.blockAllSeats}
                    label={t.model.event.blockAllSeats}
                    labels={[t.general.yes, t.general.no]}
                    onChange={(value) => setChanges({ ...changes, blockAllSeats: value })}
                  />
                )}
                <Spacer size="none" />
              </>
            )}
            {/* { Ticket Lines } */}
            <Label title={t.model.event._name} />
            {event.id && (
              <TextField
                label={t.model.event.slug}
                name="slug"
                pattern={regexToPattern(SLUG_REGEX)}
                description={t.model.event.slugFormat}
              />
            )}
            <TextField label={`${t.model.event.externalReference} 1`} name="externalReference" />
            <TextField label={`${t.model.ticket.ticketline} 1`} name="artist" required />
            <TextField label={`${t.model.ticket.ticketline} 2`} name="name" />
            <TextField label={`${t.model.ticket.ticketline} 3`} name="ticketSupportLine" />
            <TextField label={`${t.model.ticket.ticketline} ${t.general.headline}`} name="ticketHeadline" />
            <TextField label={`${t.model.ticket.ticketline} ${t.general.footer}`} name="additionalInformation.title" />
            <TextField label={`${t.model.event.tourOperator}`} name="tourOrganizer" />
            <TextField label={`${t.model.event.localOperator}`} name="localOrganizer" />
            <TextArea label={`${t.general.description}`} name="description" />
            <Switcher
              value={event.excludeFromFeeds}
              label={t.model.event.excludeFromFeeds}
              labels={[t.general.yes, t.general.no]}
              onChange={(value) => setChanges({ ...changes, excludeFromFeeds: value })}
            />
            {/* { Dates and Times } */}
            <Spacer size="none" />
            <Label title={t.model.datetime.datesAndTimes} />
            <Datepicker
              label={t.model.datetime.startDate}
              name="happeningFrom"
              disabledDays={[{ before: new Date() }]}
              required
              hourpicker
            />
            <Datepicker
              label={t.model.datetime.endDate}
              name="happeningUntil"
              disabledDays={[
                {
                  before: event.happeningFrom || new Date(),
                },
              ]}
              hourpicker
              required
            />
            {event.happeningFrom && event.happeningUntil && (
              <div>
                {t.model.datetime.duration}:{' '}
                {formatDuration(getDateRangeDuration(event.happeningFrom, event.happeningUntil), 'minutes')}
              </div>
            )}
            {startAndPublishUntilOutOfSync && calculatedPublishedUntil && (
              <div className="event-form__published-until-warning">
                <span
                  dangerouslySetInnerHTML={{
                    __html: t.model.event.publishedUntillWarning(t.model.datetime.unPublishDate),
                  }}
                />
                <Button
                  type="link"
                  title={t.model.event.updatePublishedUntil(
                    t.model.datetime.unPublishDate,
                    formatDateTime(calculatedPublishedUntil) || calculatedPublishedUntil.toString()
                  )}
                  onClick={() => updatePublishUntil()}
                />
              </div>
            )}
            <Datepicker
              label={t.model.productVariant.activeFrom}
              name={event.id ? 'productVariantsActiveFrom' : 'launchSaleAt'}
              disabledDays={[{ before: new Date() }]}
              hourpicker={true}
              errorMessage={
                uniqBy(event.productVariants, (v) => v.activeFrom.getTime()).length < 2
                  ? undefined
                  : t.model.event.productVariantsActiveFromWarning
              }
            />
            <TextField
              icon="clock--sm"
              label={`${t.model.event.doorTime}`}
              name="doorTime"
              type="custom-time"
              description={t.model.datetime.formatHHMM}
            />
            <TextField
              icon="clock--sm"
              label={`${t.model.event.stageTime}`}
              name="stageTime"
              type="custom-time"
              description={t.model.datetime.formatHHMM}
            />
            <Select
              name="personalizationRequirement"
              label={t.model.event.personalization._name}
              allowEmpty
              options={[
                {
                  label: t.model.event.personalization.noPersonalizationRequired,
                  value: 'no_personalization_required',
                },
                {
                  label: t.model.event.personalization.groupPersonalizationRequired,
                  value: 'group_personalization_required',
                },
                {
                  label: t.model.event.personalization.ticketPersonalizationRequired,
                  value: 'ticket_personalization_required',
                },
                {
                  label: t.model.event.personalization.contactTracingRequired,
                  value: 'contact_tracing_required',
                },
                {
                  label: t.model.event.personalization.checkInRequired,
                  value: 'check_in_required',
                },
                {
                  label: t.model.event.personalization.bdkv,
                  value: 'bdkv',
                },
              ]}
            />
            <TextField label={t.general.legacyId} name="legacyId" />
            {/* { Shop Settings } */}
            <Spacer size="none" />
            <Label title={t.model.event.shopSettings} />
            <Datepicker
              name="publishedFrom"
              label={t.model.datetime.publishDate}
              hourpicker
              disabledDays={[{ after: event.happeningFrom, before: new Date() }]}
            />
            {event.id && <Datepicker label={t.model.datetime.unPublishDate} name="publishedUntil" hourpicker />}
          </Stack>
        );
      }}
    </Form>
  );
}
